import {
    AfterContentInit, ContentChild, Directive, DestroyRef, Input, OnChanges, SimpleChanges, inject
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    AbstractControl,
    FormGroup,
    Validators
} from "@angular/forms";
import { MAT_DATE_FORMATS } from "@angular/material/core";
import { DateTime } from "luxon";
import {
    distinctUntilChanged
} from "rxjs";
import { MGD_DATE_FORMATS } from "../date-picker-formats";
import { DatePicker } from "../date-picker";
import {
    MatEndDate, MatStartDate
} from "@angular/material/datepicker";
import { DirectiveException } from "../../../exceptions/directive-exception";
import { TimeOfDay } from "@shopliftr/common-js/data";
import { COMMON_ERROR_HANDLER } from "../../../exceptions/constants/exception-constants";

/**
 * Input directive for date range picker
 */
@Directive({
    selector: "[mgdDateRangePicker]",
    providers: [
        {
            provide: MAT_DATE_FORMATS,
            useValue: MGD_DATE_FORMATS
        }
    ]
})
export class DateRangePickerDirective extends DatePicker implements OnChanges, AfterContentInit {
    @Input() mgdTimezone: string;

    @Input() formGroup: FormGroup;

    @ContentChild(MatStartDate) matStartDate: MatStartDate<DateTime>;

    @ContentChild(MatEndDate) matEndDate: MatEndDate<DateTime>;

    valuesChanged = false;

    private readonly _destroyRef = inject(DestroyRef);
    private readonly _errorHandler = inject(COMMON_ERROR_HANDLER);

    private _startDate: DateTime;

    private _endDate: DateTime;

    private _startControl: AbstractControl<DateTime>;

    private _endControl: AbstractControl<DateTime>;

    ngAfterContentInit(): void {

        try {

            if (!this.matStartDate) {
                throw new DirectiveException("Cannot initialize mgdDateRangePicker: matStartDate is not defined");
            }
            if (!this.matEndDate) {
                throw new DirectiveException("Cannot initialize mgdDateRangePicker: matEndDate is not defined");
            }
            this._startControl = this.matStartDate?.ngControl.control;
            this._endControl = this.matEndDate?.ngControl.control;

            this._startControl.valueChanges.pipe(
                distinctUntilChanged((previous: DateTime, current: DateTime) => this._compareFn(previous, current)),
                takeUntilDestroyed(this._destroyRef)
            ).subscribe((newStartDate: DateTime) => {

                if (newStartDate) {
                    this._startControl.markAsTouched();
                }
                if (DateTime.isDateTime(newStartDate) || !newStartDate) {
                    this._startDate = this._updateDate(newStartDate, TimeOfDay.StartOfDay);
                    if (this._startDate?.valueOf() !== newStartDate?.valueOf()) {
                        this._startControl.setValue(this._startDate);
                        // Fixed first time dismiss dialog bug
                        this._endControl.setValue(null);
                        this._endControl.addValidators(Validators.required);
                        this._endControl.updateValueAndValidity();
                    }
                }
                else {
                    this._startControl.setErrors({ notDateTime: true });
                }
            });

            this._endControl.valueChanges.pipe(
                distinctUntilChanged((previous: DateTime, current: DateTime) => this._compareFn(previous, current)),
                takeUntilDestroyed(this._destroyRef)
            ).subscribe((newEndDate: DateTime) => {

                if (newEndDate) {
                    this._endControl.markAsTouched();
                }
                if ((DateTime.isDateTime(newEndDate) || !newEndDate)) {
                    this._endDate = this._updateDate(newEndDate, TimeOfDay.EndOfDay);
                    if (this._endDate !== newEndDate) {
                        this._endControl.setValue(this._endDate);
                    }
                    // revalidate when error reset
                    this._startControl.updateValueAndValidity();
                }
                else {
                    this._endControl.setErrors({ notDateTime: true });
                }
            });

        }
        catch (error) {
            this._errorHandler.handleDirectiveError(error);
            throw error;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes.mgdTimezone) {
            if (this._startDate && this._endDate) {
                const value = {
                    start: this._updateDate(this._startControl?.value, TimeOfDay.StartOfDay),
                    end: this._updateDate(this._endControl?.value, TimeOfDay.EndOfDay)
                };
                this._startControl.setValue(value.start);
                this._endControl.setValue(value.end);
            }

        }
    }

    /**
     * Get datetime after applying timezone
     */
    private _updateDate(newValue: DateTime, timeOfDay: TimeOfDay): DateTime {

        const timezone = this.mgdTimezone ?? DateTime.local().zone;

        if (!newValue) {
            return null;
        }

        return this._coerceTime(newValue, timeOfDay, timezone);
    }
}
